/**
 * DateHelper - Utility functions for date calculations
 */

export interface DateRange {
  startDate: string; // Format: YYYY-MM-DD
  endDate: string; // Format: YYYY-MM-DD
}

/**
 * Gets a 12-month rolling date range ending today
 *
 * @returns DateRange object with startDate and endDate in YYYY-MM-DD format
 *
 * @example
 * // If today is October 10, 2025:
 * // Returns: { startDate: "2024-11-01", endDate: "2025-10-10" }
 */
export const getTwelveMonthRollingRange = (): DateRange => {
  const today = new Date();
  const currentYear = today.getFullYear();
  const currentMonth = today.getMonth(); // 0-based month (0 = January, 11 = December)

  // End date: Today
  const endDate = today.toISOString().split("T")[0];

  // Start date: First day of next month in prior year
  const startYear = currentYear - 1;
  const startMonth = currentMonth + 1; // Next month
  const startDate = new Date(startYear, startMonth, 1)
    .toISOString()
    .split("T")[0];

  return {
    startDate,
    endDate,
  };
};

/**
 * Formats a Date object to YYYY-MM-DD string format
 *
 * @param date - Date object to format
 * @returns Formatted date string in YYYY-MM-DD format
 */
export const formatDateToISO = (date: Date): string => {
  // These dates represent calendar days (billing periods), not instants — build the
  // string from local fields. toISOString() converts to UTC first, which silently
  // shifts the date by a day for any timezone ahead of UTC.
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

/**
 * Gets today's date in YYYY-MM-DD format
 *
 * @returns Today's date as YYYY-MM-DD string
 */
export const getTodayISO = (): string => {
  return formatDateToISO(new Date());
};

// Convert selected end month (YYYY-MM) into startDate and endDate strings
export const getTwelveMonthRangeFromEndMonth = (endMonthStr?: string) => {
  const today = new Date();

  // Default end date is today if no string provided
  let endDateObj = today;

  if (endMonthStr) {
    // If endMonthStr includes a day, use it directly; otherwise assume day 1
    const parts = endMonthStr.split("-").map(Number);
    const year = parts[0];
    const month = (parts[1] || 1) - 1; // JS Date 0-based
    const day = parts[2] || 1;

    endDateObj = new Date(year, month, day);

    // Ensure endDate is not in the future
    if (endDateObj > today) {
      endDateObj = today;
    }
  }

  // Start date: same day/month, one year earlier
  const startDateObj = new Date(endDateObj);
  startDateObj.setFullYear(startDateObj.getFullYear() - 1);

  return {
    startDate: formatDateToISO(startDateObj),
    endDate: formatDateToISO(endDateObj),
  };
};
